import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { Pencil, Search } from 'lucide-react';
import {
  BliksLoader,
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  Page,
  PageContent,
  PageHeader
} from '@bliks-io/ui';
import { useProcesses } from '@/api/queries/useProcesses';
import { useAuthStore } from '@/stores/auth-store';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { ContentHeader } from '@/components/content-header';
import { ProcessTabsNav } from '@/components/processes/ProcessTabsNav';
import { DepartmentRail } from './DepartmentRail';
import { EditorProcessRow } from './EditorProcessRow';
import { groupEditableByDepartment, visibleProcesses } from './editorDepartments';

export function ProcessEditorLandingPage() {
  const { t, i18n } = useTranslation();
  useDocumentTitle('pageTitles.processEditor');
  const slug = useAuthStore().slug();
  const { data: processes, isLoading } = useProcesses();
  const { department: departmentParam } = useSearch({ from: '/$slug/processes/editor/' });
  const navigate = useNavigate({ from: '/$slug/processes/editor/' });
  const [query, setQuery] = useState('');

  const noDepartmentLabel = t('organization.orgFunctions.noDepartment');
  const departments = useMemo(
    () => groupEditableByDepartment(processes ?? [], noDepartmentLabel, i18n.language),
    [processes, noDepartmentLabel, i18n.language]
  );
  const selectedDepartment = departments.find((department) => department.id === departmentParam);

  const rows = visibleProcesses(departments, selectedDepartment, query, i18n.language);

  const selectDepartment = (departmentId: string | undefined) => {
    void navigate({ search: { department: departmentId }, replace: true });
  };

  const emptyState = (
    <p className="py-4 text-center text-sm text-muted-foreground">{t('processes.empty')}</p>
  );

  return (
    <Page>
      <PageHeader>
        <ProcessTabsNav />
      </PageHeader>
      <PageContent className="flex flex-col overflow-hidden!">
        <ContentHeader icon={Pencil} title={t('common.Sidebar.ProcessEditor')} />

        {isLoading && (
          <div className="mt-8 py-4">
            <BliksLoader label={t('common.loading')} />
          </div>
        )}
        {!isLoading && departments.length === 0 && <div className="mt-8">{emptyState}</div>}
        {!isLoading && departments.length > 0 && (
          <div className="mt-8 flex min-h-0 flex-1 gap-8">
            <DepartmentRail
              departments={departments}
              selectedId={selectedDepartment?.id}
              onSelect={selectDepartment}
            />
            <div className="flex min-h-0 min-w-0 flex-1 flex-col">
              <InputGroup className="shrink-0">
                <InputGroupAddon align="inline-start">
                  <Search />
                </InputGroupAddon>
                <InputGroupInput
                  placeholder={t('processes.search')}
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                />
              </InputGroup>
              <div
                key={selectedDepartment?.id ?? 'all'}
                className="mt-5 -mx-1 -mb-1 min-h-0 flex-1 scroll-py-1 overflow-y-auto p-1"
              >
                {rows.length === 0 ? (
                  emptyState
                ) : (
                  <ul className="flex flex-col gap-1">
                    {rows.map(({ process, departmentName }) => (
                      <li key={process._id}>
                        <EditorProcessRow
                          slug={slug}
                          process={process}
                          departmentName={departmentName}
                        />
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>
        )}
      </PageContent>
    </Page>
  );
}
